// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The model: two anonymous GETs of public files, pinned to an immutable
 * revision, cached after the first run so every later session is fully offline.
 *
 * These are the ONLY requests topicgap makes that are not for its own assets,
 * and they carry nothing about the user: the URL is a constant, the method is
 * GET, and `credentials: 'omit'` keeps any Hugging Face cookie out of it. Your
 * rows are never part of a request — there is no code path that could put them
 * in one.
 *
 * ── WHY THIS MODEL AND NOT THE ONE THE SPEC NAMED ────────────────────────────
 * The queued spec asked for `sentence-transformers/static-retrieval-mrl-en-v1`,
 * MRL-truncated to 256 dims. Three things are wrong with that for THIS job and
 * the research pass established all three first-hand:
 *
 * 1. It is close to the worst available choice for CLUSTERING. On MTEB's
 *    StackExchangeClustering.v2 — short, user-written, one-question-per-row,
 *    which is the closest public analogue to a support queue — it scores 30.39
 *    v-measure. potion-base-4M scores 50.91 on the same task and beats
 *    all-MiniLM-L6-v2's 45.17, the six-layer transformer Apple's Embedding Atlas
 *    runs in the browser for the same job. The spec's model is not merely worse,
 *    it is unstable on this task shape: v_measure_std 0.0614 against potion's
 *    0.0099 on TwentyNewsgroups.
 * 2. It is half the size to download — 15.1 MB against 31.3 MB.
 * 3. It is f32 with 128 NATIVE dimensions, so there is no dequantisation, no
 *    zero point, no scale, and none of the MRL truncation loss.
 *
 * `apply_zipf` in its config.json is a record of DISTILLATION, not a runtime
 * flag: the Zipf weighting and the PCA are already baked into the table, so
 * inference really is lookup -> mean -> L2 and nothing else.
 */

export const MODEL_REPO = 'minishlab/potion-base-4M';
/** Pinned so the bytes can never change under us. */
export const MODEL_REV = '9b3cff412d30be9ae8603fe10224c224f3401869';
export const MODEL_LICENSE = 'MIT';

const BASE = `https://huggingface.co/${MODEL_REPO}/resolve/${MODEL_REV}`;
export const TABLE_URL = `${BASE}/model.safetensors`;
export const TOKENIZER_URL = `${BASE}/tokenizer.json`;

/** Verified byte counts, used only for a progress total when no content-length arrives. */
export const TABLE_BYTES = 15_118_424;
export const TOKENIZER_BYTES = 683_666;

export const CACHE_NAME = 'topicgap-model-v1';

export interface FetchProgress {
  url: string;
  received: number;
  total: number;
  fromCache: boolean;
}

async function openCache(): Promise<Cache | null> {
  try {
    if (typeof caches === 'undefined') return null;
    return await caches.open(CACHE_NAME);
  } catch {
    return null;
  }
}

/**
 * Fetch a URL with byte-level progress, serving from the Cache API when we have
 * it. A cache miss populates the cache so the tool works offline afterwards.
 */
export async function fetchCached(
  url: string,
  expectedBytes: number,
  onProgress: (p: FetchProgress) => void,
  signal?: AbortSignal,
): Promise<ArrayBuffer> {
  const cache = await openCache();
  if (cache) {
    const hit = await cache.match(url);
    if (hit) {
      const buf = await hit.arrayBuffer();
      onProgress({ url, received: buf.byteLength, total: buf.byteLength, fromCache: true });
      return buf;
    }
  }

  const res = await fetch(url, { signal, credentials: 'omit', mode: 'cors' });
  if (!res.ok) {
    throw new Error(`Could not download the model — HTTP ${res.status} ${res.statusText}`);
  }

  const declared = Number(res.headers.get('content-length') ?? 0);
  const total = declared > 0 ? declared : expectedBytes;

  let bytes: Uint8Array;
  if (res.body) {
    const reader = res.body.getReader();
    const parts: Uint8Array[] = [];
    let received = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        parts.push(value);
        received += value.byteLength;
        onProgress({ url, received, total, fromCache: false });
      }
    }
    bytes = new Uint8Array(received);
    let at = 0;
    for (const p of parts) {
      bytes.set(p, at);
      at += p.byteLength;
    }
  } else {
    bytes = new Uint8Array(await res.arrayBuffer());
    onProgress({ url, received: bytes.byteLength, total, fromCache: false });
  }

  // A fresh, exactly-sized buffer: the Float32Array view over the table assumes
  // it owns offset 0 of this ArrayBuffer.
  const out = bytes.buffer.slice(0) as ArrayBuffer;

  if (cache) {
    try {
      await cache.put(
        url,
        new Response(out.slice(0), { headers: { 'content-type': 'application/octet-stream' } }),
      );
    } catch {
      /* a full or unavailable cache must not fail the run */
    }
  }

  return out;
}
